import { addressInputs } from './addressRoles.js'

/**
 * Record forms: which fields appear, in which groups, and how a record (the API resource)
 * turns into form inputs and back into a request body. Fields and custom fields come from
 * Core's meta; this only arranges them.
 */

const MEETING_ONLY = ['start_at', 'end_at', 'all_day', 'location']

export const FORM_LAYOUT = {
  account: [
    { key: 'main', fields: ['name', 'short_code', 'type', 'industry', 'parent_account', 'website', 'status'] },
    // Where it is registered and its numbers there, a registry filling them (D-199).
    { key: 'registration', fields: ['registration_country', 'tax_id', 'regon', 'krs'] },
    { key: 'contact_points', fields: ['contact_points'], wide: true },
    { key: 'address', fields: ['addresses', 'time_zone'], wide: true },
    { key: 'more', fields: ['description', 'tags', 'owner', 'is_private'] },
  ],
  contact: [
    { key: 'main', fields: ['first_name', 'last_name', 'account', 'job_title', 'reports_to', 'department', 'preferred_language', 'do_not_call', 'status'] },
    { key: 'contact_points', fields: ['contact_points'], wide: true },
    { key: 'address', fields: ['addresses', 'time_zone'], wide: true },
    { key: 'more', fields: ['description', 'tags', 'owner', 'is_private'] },
  ],
  lead: [
    { key: 'main', fields: ['topic', 'source', 'partner', 'campaign', 'event', 'rating', 'company_name', 'registration_country', 'tax_id', 'first_name', 'last_name', 'job_title', 'email', 'phone', 'account', 'contact', 'pipeline', 'stage'] },
    { key: 'qualification', fields: ['purchase_timeframe', 'budget_amount', 'purchase_process', 'decision_maker_identified', 'qualification_summary'] },
    { key: 'interests', fields: ['product_interests'], wide: true },
    { key: 'address', fields: ['address'] },
    { key: 'more', fields: ['description', 'tags', 'owner', 'is_private'] },
  ],
  opportunity: [
    { key: 'main', fields: ['name', 'account', 'end_customer', 'contact', 'pipeline', 'stage', 'estimated_value', 'probability', 'estimated_close_date', 'rating', 'campaign', 'referred_by'] },
    { key: 'qualification', fields: ['purchase_timeframe', 'budget_amount', 'purchase_process', 'decision_maker_identified', 'qualification_summary'] },
    { key: 'develop', fields: ['customer_need', 'proposed_solution'] },
    { key: 'interests', fields: ['product_interests'], wide: true },
    { key: 'more', fields: ['description', 'tags', 'owner', 'is_private'] },
  ],
  activity: [
    { key: 'main', fields: ['type', 'subtype', 'subject', 'regarding', 'priority', 'direction', 'completed', 'due_at', 'start_at', 'end_at', 'all_day', 'location', 'recurrence_rule'] },
    { key: 'parties', fields: ['parties'], wide: true },
    { key: 'description', fields: ['description'], wide: true },
    { key: 'closing', fields: ['outcome', 'cancel_reason', 'actual_duration_minutes'] },
    { key: 'more', fields: ['tags', 'owner', 'is_private'] },
  ],
  note: [
    { key: 'main', fields: ['noteable', 'body', 'is_pinned', 'tags', 'is_private'], wide: true },
  ],
  // A contract (D-175): its parties, its realisation; its value and versions come from offers.
  contract: [
    { key: 'main', fields: ['title', 'account', 'end_customer', 'contact', 'opportunity', 'stage'] },
    { key: 'realisation', fields: ['signed_on', 'starts_on', 'ends_on', 'current_version_no'] },
    { key: 'description', fields: ['description'], wide: true },
    { key: 'more', fields: ['tags', 'owner', 'is_private'] },
  ],
  // An agreement of a contract (D-176): what it is, a service agreement's terms, its figures.
  agreement: [
    { key: 'main', fields: ['title', 'number', 'contract', 'agreement_type', 'status', 'signed_on', 'due_on', 'ends_on'] },
    { key: 'terms', fields: ['starts_on', 'min_term_months', 'notice_months', 'renewal', 'renewal_months', 'indexation_rule', 'notice_given_on'] },
    { key: 'figures', fields: ['value', 'accepted_value', 'monthly_fee', 'monthly_active'] },
    { key: 'description', fields: ['note'], wide: true },
    { key: 'more', fields: ['tags', 'owner', 'is_private'] },
  ],
  campaign: [
    { key: 'main', fields: ['name', 'type', 'status', 'proposed_start', 'proposed_end', 'actual_start', 'actual_end'] },
    { key: 'money', fields: ['budget', 'other_costs', 'expected_revenue', 'expected_response_rate'] },
    { key: 'description', fields: ['offer', 'description'], wide: true },
    { key: 'more', fields: ['tags', 'owner', 'is_private'] },
  ],
  marketing_list: [
    { key: 'main', fields: ['name', 'member_type', 'list_type', 'purpose', 'source', 'locked'] },
    { key: 'condition', fields: ['condition'], wide: true },
    { key: 'more', fields: ['description', 'tags', 'owner', 'is_private'] },
  ],
  event: [
    { key: 'main', fields: ['name', 'type', 'status', 'start_at', 'end_at', 'location', 'online_url', 'capacity', 'campaign', 'cost'] },
    { key: 'more', fields: ['description', 'tags', 'owner', 'is_private'] },
  ],
  competitor: [
    { key: 'main', fields: ['name', 'website', 'ticker_symbol', 'reported_revenue', 'is_active', 'key_products', 'overview'] },
    { key: 'swot', fields: ['strengths', 'weaknesses', 'threats'] },
    { key: 'more', fields: ['tags', 'owner', 'is_private'] },
  ],
  // A sales material (D-332): what it is, kept by whom till when; its files, links and what it fits in cards of its own.
  sales_material: [
    { key: 'main', fields: ['name', 'category', 'review_date', 'is_internal', 'is_active', 'description'] },
    { key: 'more', fields: ['tags', 'owner', 'is_private'] },
  ],
  // In the order an offer is made: what it is, for whom (and where), how its document looks (D-150).
  offer: [
    { key: 'main', fields: ['title', 'template', 'modules', 'price_list'] },
    { key: 'client', fields: ['opportunity', 'account', 'contact', 'end_customer', 'site_address', 'other_site_addresses'] },
    { key: 'offer_document', fields: ['submission', 'character', 'document_date', 'valid_until', 'language', 'currency_code', 'branding_set'] },
    // The customer's request it answers (D-358), whichever way it goes back.
    { key: 'request', fields: ['customer_reference', 'submission_deadline', 'questions_deadline'] },
    { key: 'more', fields: ['owner', 'is_private'] },
  ],
}

/**
 * The form's sections for an entity (D-133): the tenant's saved layout, or the built-in one.
 * Each: { key, label ({pl, en} or null for a built-in key), wide, fields } with custom fields
 * named "cf.key" as forms name them. Custom fields not placed by the layout go to the end of
 * the section their `section` names (by key, or by its title as fields once named it), else to
 * a section of that name, else to the first; those the layout leaves off stay off.
 */
export function layoutSections(entity, meta, layout = undefined) {
  // `layout`: a stored layout to lay out (the editor's); none given: the one meta says applies.
  const saved = layout === undefined ? meta.data?.form_layouts?.[entity] : layout
  const toForm = (name) => (name.startsWith('cf:') ? `cf.${name.slice(3)}` : name)
  const sections = saved
    ? saved.sections.map((s) => ({ key: s.key, label: s.label || null, wide: !!s.wide, fields: s.fields.map(toForm) }))
    : (FORM_LAYOUT[entity] || [{ key: 'main', fields: meta.fields(entity).map((f) => f.name) }]).map((g) => ({ key: g.key, label: null, wide: !!g.wide, fields: [...g.fields] }))
  const off = new Set((saved?.hidden || []).map(toForm))
  const placed = new Set(sections.flatMap((s) => s.fields))
  // A field the system gained after the layout was saved (the layout keeps every field it took off):
  // into its built-in section where the layout has it, else the first.
  if (saved) {
    for (const group of FORM_LAYOUT[entity] || []) {
      for (const name of group.fields) {
        if (placed.has(name) || off.has(name)) continue
        ;(sections.find((s) => s.key === group.key) || sections[0])?.fields.push(name)
        placed.add(name)
      }
    }
  }
  for (const cf of meta.customFields(entity)) {
    const name = `cf.${cf.key}`
    if (placed.has(name) || off.has(name)) continue
    const wanted = (cf.section || '').trim()
    let target = wanted ? sections.find((s) => s.key === wanted || [s.label?.pl, s.label?.en].some((l) => l && l.toLowerCase() === wanted.toLowerCase())) : sections[0]
    if (!target) {
      // A key the layout can be saved with: "Profil firmy" → "cf-profil_firmy".
      const slug = wanted.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ł/gi, 'l').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
      target = { key: `cf-${slug || 'section'}`, label: { pl: wanted, en: wanted }, wide: false, fields: [] }
      sections.splice(Math.max(1, sections.length - 1), 0, target)
    }
    target.fields.push(name)
  }
  return sections
}

/** A section's title: its own name, else the built-in one for its key. */
export function sectionTitle(section, meta, t) {
  if (section.label && (section.label.pl || section.label.en)) return meta.localized(section.label)
  if (section.key === 'main') return ''
  return t(`form.groups.${section.key}`, section.key)
}

/** Fields that are not plain entity fields but have their own input. */
export const SPECIAL_FIELDS = ['contact_points', 'addresses', 'tags', 'owner', 'is_private', 'completed', 'product_interests', 'regarding', 'noteable', 'parties', 'template', 'modules']

/** Only when creating: an offer is built once from a template and modules. */
export const CREATE_ONLY = ['template', 'modules', 'completed']

/** Wide inputs span the full form width. */
export const WIDE_TYPES = ['text', 'richtext', 'address', 'textarea']

/** Is this field shown for the record as it is now (activity type decides some)? */
const AGREEMENT_SERVICE_ONLY = ['starts_on', 'min_term_months', 'notice_months', 'renewal', 'renewal_months', 'indexation_rule', 'notice_given_on', 'monthly_fee', 'monthly_active']

export function fieldApplies(entity, name, form) {
  if (entity === 'activity') {
    const type = form.type || 'task'
    // A meeting's end is set beside its start (they move together).
    if (name === 'end_at') return false
    if (MEETING_ONLY.includes(name)) return type === 'meeting'
    if (name === 'due_at') return type !== 'meeting'
    if (name === 'direction') return type === 'call' || type === 'email'
    if (name === 'parties') return type !== 'task'
    // How it was closed: shown once it is (Dynamics' Close Activity); asked when made already over (D-351).
    const closed = form.status && form.status !== 'open'
    if (name === 'outcome') return (closed && form.status === 'completed') || (!form.status && !!form.completed)
    if (name === 'cancel_reason') return closed && form.status === 'cancelled'
    if (name === 'actual_duration_minutes') return closed || (!form.status && !!form.completed)
    // The recurrence of what is over: none.
    if (name === 'recurrence_rule' && form.completed) return false
  }
  // An agreement (D-176): a service agreement's terms and fees only on one, an accepted value only on
  // an execution agreement; a new one (its kind not chosen yet) asks only what it is.
  if (entity === 'agreement') {
    // Its kind follows its type: set by the type chosen in the form, or the record's.
    const kind = form.kind || null
    if (AGREEMENT_SERVICE_ONLY.includes(name)) return kind === 'service' && (name !== 'renewal_months' || form.renewal === 'fixed')
    if (name === 'accepted_value' || name === 'due_on') return kind === 'execution'
    if (name === 'value') return kind !== null
  }
  // A marketing list's condition is the dynamic list's (D-139).
  if (entity === 'marketing_list' && name === 'condition') return form.list_type === 'dynamic'
  if (entity === 'opportunity' && name === 'probability') return true
  return true
}

const RECURRENCES = ['', 'FREQ=DAILY', 'FREQ=WEEKLY', 'FREQ=MONTHLY']
export { RECURRENCES }

/** The API resource → form inputs (the shape the API takes). */
export function toInputs(entity, record, meta) {
  const inputs = {}
  const refs = {}
  for (const field of meta.fields(entity)) {
    if (field.type === 'morph') continue
    if (field.type === 'ref') {
      inputs[field.input] = record?.[field.input] ?? record?.[field.name]?.uuid ?? null
      refs[field.name] = record?.[field.name] || null
    } else {
      inputs[field.name] = record?.[field.name] ?? null
    }
  }
  inputs.custom_fields = { ...(record?.custom_fields || {}) }
  inputs.owner = record?.owner ? { type: record.owner.type, uuid: record.owner.uuid, label: record.owner.label } : null
  inputs.is_private = !!record?.is_private
  inputs.tags = (record?.tags || []).map((t) => t.uuid)
  if (entity === 'account' || entity === 'contact') {
    inputs.contact_points = (record?.contact_points || []).map((p) => ({ uuid: p.uuid, channel: p.channel, kind: p.kind, label: p.label ?? null, value: p.value, is_primary: !!p.is_primary }))
    // D-198: the addresses with their roles; a contact's pointing at its account's say which.
    inputs.addresses = addressInputs(record?.addresses)
  }
  if (entity === 'lead' || entity === 'opportunity') {
    inputs.product_interests = (record?.product_interests || []).map((i) => ({ product_uuid: i.module ? null : i.product?.uuid ?? null, module_uuid: i.module?.uuid ?? null, note: i.note ?? null }))
  }
  if (entity === 'activity') {
    inputs.regarding = record?.regarding ? { entity: record.regarding_type || record.regarding.entity, uuid: record.regarding.uuid } : null
    refs.regarding = record?.regarding ? { ...record.regarding, entity: record.regarding_type || record.regarding.entity } : null
    inputs.parties = (record?.parties || []).map((p) => (p.party ? { role: p.role, entity: p.party.entity, uuid: p.party.uuid, label: p.party.label } : { role: p.role, email: p.email, name: p.name }))
  }
  if (entity === 'offer' && !record?.uuid) {
    inputs.template_uuid = null
    inputs.module_uuids = []
  }
  if (entity === 'note') {
    inputs.noteable = record?.noteable ? { entity: record.noteable_type || record.noteable.entity, uuid: record.noteable.uuid } : null
    refs.noteable = record?.noteable ? { ...record.noteable, entity: record.noteable_type || record.noteable.entity } : null
  }
  return { inputs, refs }
}

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/**
 * The request body: everything for a new record, only what changed for an existing one
 * (so a conflict is detected on the fields actually edited, D-38). Custom fields are
 * compared key by key.
 */
export function bodyFrom(entity, inputs, original, meta, { creating }) {
  const body = {}
  const readonly = new Set(meta.fields(entity).filter((f) => f.readonly).map((f) => f.input || f.name))
  for (const [key, value] of Object.entries(inputs)) {
    if (readonly.has(key) || key === 'custom_fields') continue
    if (creating) {
      if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) body[key] = key === 'owner' && value ? { type: value.type, uuid: value.uuid } : value
    } else if (!same(value, original?.[key])) {
      body[key] = key === 'owner' && value ? { type: value.type, uuid: value.uuid } : value
    }
  }
  const derived = new Set(meta.customFields(entity).filter((f) => ['borrowed', 'computed'].includes(f.type)).map((f) => f.key))
  const custom = {}
  for (const [key, value] of Object.entries(inputs.custom_fields || {})) {
    if (derived.has(key)) continue
    if (creating ? value !== null && value !== '' : !same(value, original?.custom_fields?.[key])) custom[key] = value === '' ? null : value
  }
  if (Object.keys(custom).length) body.custom_fields = custom
  return body
}
